/* eslint-disable @typescript-eslint/no-explicit-any */
import { Injectable } from '@angular/core';
import {
  Badge,
  Employee,
  EmployeeAssignmentHistory,
  EmployeeBadge,
  EmployeeSkill,
  EmployeeEducationHistory,
  EmployeePositionHistory,
  User,
} from '@core/types/core';
import { GetUserQuery, ListUsersQuery } from 'kms-amplify/src';

@Injectable({
  providedIn: 'root',
})
export class DataMappingService {
  // constructor() { }

  getUser(gqlUser: GetUserQuery['getUser']): User | null {
    if (gqlUser) {
      return {
        id: gqlUser.id,
        name: gqlUser.name,
        username: gqlUser.username,
        email: gqlUser.email,
      };
    }
    return null;
  }

  listUser(gqlUsers: ListUsersQuery['listUsers']): Array<User | null> {
    if (gqlUsers) {
      return gqlUsers.items.map(gqlUser => {
        if (gqlUser) {
          return {
            id: gqlUser.id,
            name: gqlUser.name,
            username: gqlUser.username,
            email: gqlUser.email,
          };
        }
        return null;
      });
    }
    return [];
  }

  mapUser(user?: any): User | null {
    if (user) {
      return {
        id: user.id,
        name: user.name,
        username: user.username,
        email: user.email,
      };
    }
    return null;
  }

  mapPositionHistory(positionHistory: any): EmployeePositionHistory | null {
    if (positionHistory) {
      const res: EmployeePositionHistory = {
        dateFrom: positionHistory.dateFrom,
        dateUntil: positionHistory.dateUntil,
        title: positionHistory.title,
        description: positionHistory.description,
        employeeId: positionHistory.employeeId,
        internalPosition: positionHistory.internalPosition,
        location: positionHistory.location,
        officeName: positionHistory.officeName,
      };
      return res;
    }
    return null;
  }

  mapAssignmentHistory(assignmentHistory: any): EmployeeAssignmentHistory | null {
    if (assignmentHistory) {
      const res: EmployeeAssignmentHistory = {
        dateFrom: assignmentHistory.dateFrom,
        dateUntil: assignmentHistory.dateUntil,
        title: assignmentHistory.title,
        description: assignmentHistory.description,
        employeeId: assignmentHistory.employeeId,
        bodRelated: assignmentHistory.bodRelated,
        location: assignmentHistory.location,
        officeName: assignmentHistory.officeName,
      };
      return res;
    }
    return null;
  }

  mapBadge(badge: any): Badge | null {
    if (badge) {
      const res: Badge = {
        name: badge.name,
        description: badge.description,
        iconUrl: badge.iconUrl,
      };
      return res;
    }
    return null;
  }

  mapEmployeeBadge(employeeBadge: any): EmployeeBadge | null {
    if (employeeBadge) {
      const res: EmployeeBadge = {
        id: employeeBadge.id,
        employeeId: employeeBadge.employeeId,
        badge: this.mapBadge(employeeBadge.badge) as Badge,
        grantDate: employeeBadge.grantDate,
        grantedBy: this.mapEmployee(employeeBadge.grantedBy) as Employee,
      };
      return res;
    }
    return null;
  }

  mapEmployeeSkills(employeeSkills: any): EmployeeSkill | null {
    if (employeeSkills) {
      const res: EmployeeSkill = {
        skillGroup: employeeSkills.skillGroup,
        skill: employeeSkills.skill,
        name: employeeSkills.name,
        level: employeeSkills.level,
        progress: employeeSkills.progress,
      };
      return res;
    }
    return null;
  }

  mapEducationHistory(educationHistory: any): EmployeeEducationHistory | null {
    if (educationHistory) {
      const res: EmployeeEducationHistory = {
        employeeId: educationHistory.employeeId,
        schoolName: educationHistory.schoolName,
        entryYear: educationHistory.entryYear,
        graduationYear: educationHistory.graduationYear,
      };
      return res;
    }
    return null;
  }

  mapEmployee(employee?: any): Employee | null {
    if (employee) {
      const employeeRes: Employee = { id: employee.id };
      if (employee.activities) employeeRes.activities = employee.activities;
      if (employee.domicile) employeeRes.domicile = employee.domicile;
      if (employee.nik) employeeRes.nik = employee.nik;
      if (employee.objectives) employeeRes.objectives = employee.objectives;
      if (employee.organizationUnit) employeeRes.organizationUnit = employee.organizationUnit;
      if (employee.organizationUnitId) employeeRes.organizationUnitId = employee.organizationUnitId;
      if (employee.rewards) employeeRes.rewards = employee.rewards;
      if (employee.skills) employeeRes.skills = employee.skills;
      if (employee.socialMedias) employeeRes.socialMedias = employee.socialMedias;
      if (employee.status) employeeRes.status = employee.status;
      if (employee.trainings) employeeRes.trainings = employee.trainings;

      const mappedUser = this.mapUser(employee.user);
      if (mappedUser) employeeRes.user = mappedUser;

      if (employee.jobTitles) {
        employeeRes.jobTitles = (employee?.jobTitles?.items || []).map((el: any) => el.jobTitle);
      }

      if (employee.manager) {
        employeeRes.manager = this.mapEmployee(employee.manager) as Employee;
      }

      if (employee.positionHistories) {
        employeeRes.positionHistories = (employee?.positionHistories?.items || []).map((el: any) =>
          this.mapPositionHistory(el)
        ) as [EmployeePositionHistory];
      }

      if (employee.educationHistories) {
        employeeRes.educationHistories = (employee.educationHistories?.items || []).map((el: any) =>
          this.mapEducationHistory(el)
        );
      }

      if (employee.assignmentHistories) {
        employeeRes.assignmentHistories = (employee.assignmentHistories?.items || []).map(
          (el: any) => this.mapAssignmentHistory(el)
        );
      }

      if (employee.badges) {
        employeeRes.badges = (employee.badges?.items || []).map((el: any) =>
          this.mapEmployeeBadge(el)
        );
      }

      if (employee.skills) {
        employeeRes.skills = (employee.skills?.items || []).map((el: any) =>
          this.mapEmployeeSkills(el)
        );
      }

      return employeeRes;
    }
    return null;
  }
}
